import React, { useEffect, useMemo, useState } from 'react';
import { DocumentIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import type { TemplateForm, TemplatePhase } from './templateManagementService';
import { courtIntelligenceApi } from '@/features/matters/api/courtIntelligenceApi';
import { PdfPreviewPanel } from '@/shared/components/PdfPreviewPanel';
import { WeBelieveLogger } from '@/utils/WeBelieveLogger';

interface TemplateDetailFormsProps {
  phases: TemplatePhase[];
}

interface FormRow extends TemplateForm {
  key: string;
}

interface CatalogEntry {
  id: string;
  pdf_url: string | null;
  detail_url: string | null;
  form_name: string;
  lifecycle_status: string;
}

const TemplateDetailForms: React.FC<TemplateDetailFormsProps> = ({ phases }) => {
  const [catalogUrls, setCatalogUrls] = useState<Record<string, CatalogEntry>>({});
  const [lookupDone, setLookupDone] = useState(false);
  const [preview, setPreview] = useState<{ id: string; label: string } | null>(null);

  const forms = useMemo<FormRow[]>(() => {
    const map = new Map<string, FormRow>();
    for (const phase of phases) {
      for (const task of phase.tasks) {
        for (const form of task.required_forms || []) {
          const key = `${(form.form_number || '').trim().toLowerCase()}|${(form.form_name || '').trim().toLowerCase()}`;
          if (!key || key === '|') continue;
          if (!map.has(key)) {
            map.set(key, { ...form, key });
          }
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => a.form_number.localeCompare(b.form_number));
  }, [phases]);

  // Resolve PDF URLs from the court_forms catalog
  useEffect(() => {
    if (forms.length === 0) return;

    const formNumbers = forms.map((f) => f.form_number);
    let cancelled = false;

    courtIntelligenceApi
      .lookupFormUrls(formNumbers)
      .then((result) => {
        if (!cancelled) {
          setCatalogUrls(result);
          setLookupDone(true);
        }
      })
      .catch((err) => {
        WeBelieveLogger.warn('TemplateDetailForms', 'PDF URL lookup failed', { error: err?.message });
        if (!cancelled) setLookupDone(true);
      });

    return () => {
      cancelled = true;
    };
  }, [forms]);

  if (forms.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">No required forms are attached to this template yet.</p>;
  }

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">Form #</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">Name</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">Issuing Body</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">Required</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">Status</th>
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-800 dark:bg-gray-900">
            {forms.map((form) => {
              const catalogKey = form.form_number.trim().toUpperCase();
              const catalog = catalogUrls[catalogKey];
              const courtFormId = catalog?.id || null;
              const pdfUrl = form.url || catalog?.pdf_url || null;
              const detailUrl = catalog?.detail_url || null;
              const lifecycleStatus = catalog?.lifecycle_status;

              return (
                <tr key={form.key} className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                  <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">{form.form_number}</td>
                  <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{form.form_name}</td>
                  <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">{form.issuing_body || 'N/A'}</td>
                  <td className="px-4 py-2 text-sm">
                    {form.is_mandatory ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                        Required
                      </span>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400 text-xs">Optional</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm">
                    {lookupDone && lifecycleStatus ? (
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                        lifecycleStatus === 'validated'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : lifecycleStatus === 'stale'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
                      }`}>
                        {lifecycleStatus === 'validated' ? 'In Catalog' : lifecycleStatus === 'stale' ? 'Stale' : 'Draft'}
                      </span>
                    ) : lookupDone ? (
                      <span className="text-gray-400 dark:text-gray-500 text-xs">Not in catalog</span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500 text-xs">Loading...</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm">
                    <div className="flex items-center gap-2">
                      {courtFormId ? (
                        <button
                          onClick={() => setPreview({ id: courtFormId, label: `${form.form_number} — ${form.form_name}` })}
                          className="inline-flex items-center gap-1 font-medium text-lex-accent hover:text-lex-accent-hover dark:text-lex-accent dark:hover:text-lex-accent text-xs transition-colors"
                        >
                          <DocumentIcon className="h-3.5 w-3.5" />
                          View PDF
                        </button>
                      ) : pdfUrl ? (
                        <a
                          href={pdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 font-medium text-lex-accent hover:text-lex-accent-hover dark:text-lex-accent dark:hover:text-lex-accent text-xs"
                        >
                          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                          View PDF
                        </a>
                      ) : lookupDone ? (
                        <span className="text-gray-400 dark:text-gray-500 text-xs">No PDF</span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 text-xs">...</span>
                      )}
                      {detailUrl && (
                        <a
                          href={detailUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 text-xs"
                        >
                          Info
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {preview && (
        <PdfPreviewPanel
          courtFormId={preview.id}
          label={preview.label}
          onClose={() => setPreview(null)}
          source="TemplateDetailForms"
        />
      )}
    </>
  );
};

export default TemplateDetailForms;
